import type { DictFragment } from '../translations';

/** Échéances (deadlines): the Gérance PMO overview and shared deadline vocabulary. */
export const echeances: DictFragment = {
  fr: {
    'gerance.title': 'Gérance',
    'gerance.subtitle': 'Vue consolidée des échéances de tous les projets',
    'gerance.view.calendar': 'Vue calendrier',
    'gerance.view.list': 'Vue liste',
    'gerance.view.involvements': 'Implications',

    // [Proposition: implications]
    'involvements.intro': 'Les tâches et projets auxquels quelqu’un d’autre vous a associé·e. Vous en êtes aussi prévenu·e par e-mail.',
    'involvements.introAdmin': 'Qui a été associé à quelle tâche ou quel projet, avec quel rôle, et par qui. Chaque personne en est aussi prévenue par e-mail.',
    'involvements.type.task': 'Tâches',
    'involvements.type.project': 'Projets',
    'involvements.allPersons': 'Toutes les personnes',
    'involvements.projectItem': 'Le projet',
    'involvements.col.date': 'Date',
    'involvements.col.person': 'Personne',
    'involvements.col.item': 'Implication',
    'involvements.col.role': 'Rôle',
    'involvements.col.by': 'Par',
    'involvements.emptyTitle': 'Aucune implication',
    'involvements.emptyDesc': 'Rien à afficher pour le moment.',

    'gerancemeta.emptyTitle': 'Aucun projet en cours',
    'gerancemeta.emptyDesc': 'Aucun projet en Planning ou en Exécution pour le moment.',
    'gerancemeta.tasksTitle': 'Tâches ouvertes ({n})',
    'gerancemeta.taskBlocked': 'Tâche bloquée',
    'gerancemeta.milestoneStateDone': 'Jalon complété',
    'gerancemeta.milestoneUrgencyGreen': 'En cours · à jour',
    'gerancemeta.milestoneUrgencyOrange': 'En cours · bientôt dû',
    'gerancemeta.milestoneUrgencyRed': 'En cours · en retard',
    'gerancemeta.milestoneStatePending': 'Jalon à venir',
    'gerancemeta.projectDeadline': 'Échéance du projet',
    'gerancemeta.rcajoScoreLine': 'RCAJO :',
    'gerancemeta.rcajoNotLinked': 'Non lié',
    'gerancemeta.rcajoNoScore': 'Note non renseignée',
    'gerancemeta.milestoneInfoBtn': 'Voir les détails du jalon',
    'gerancemeta.milestoneDates': '{start} → {end}',
    'gerancemeta.milestoneNoDescription': 'Aucune description.',
    'gerancemeta.milestoneOpenTasks': 'Tâches ouvertes',
    'gerancemeta.milestoneNoOpenTasks': 'Aucune tâche ouverte.',
    'gerancemeta.viewProject': 'Voir le projet',
    'gerancemeta.markMeeting': 'Séance tenue',
    'gerancemeta.markTaskDone': 'Tâche terminée',
    'gerancemeta.markMeetingsTitle': '{n} séance(s)',
    'gerancemeta.markTasksDoneTitle': '{n} tâche(s) passée(s) en Terminé',
    'gerancemeta.timeSliderHint': 'Remonter dans le temps : la vue est redessinée telle qu’elle était à cette date',
    'gerancemeta.timeSliderReset': 'Revenir à aujourd’hui',
    'gerancemeta.absentAt': 'Le {date}, ce projet n’était pas en Planning/Exécution (étape : {stage})',
    'gerancemeta.notCreatedAt': 'Le {date}, ce projet n’existait pas encore',
    'gerancemeta.cardMeetings': 'Séances ({n})',
    'gerancemeta.cardTasksDone': 'Tâches terminées ({n})',
    'gerancemeta.cardMore': '+ {n} autres',
    'gerancemeta.railTotalsHint': 'Séances tenues et tâches terminées depuis le {date}',

    'echeances.type.project': 'Projet',
    'echeances.type.milestone': 'Jalon',
    'echeances.type.task': 'Tâche',
    'echeances.filterAllTypes': 'Tous',
    'echeances.filterAllProjects': 'Tous les projets',
    'echeances.overdueOnly': 'En retard uniquement',
    'echeances.stat.overdue': 'En retard',
    'echeances.stat.overdueHint': 'Afficher uniquement les échéances en retard',
    'echeances.daysLate': '+{n} j',
    'echeances.stat.dueWeek': 'Cette semaine',
    'echeances.stat.dueMonth': 'Ce mois-ci',
    'echeances.col.date': 'Échéance',
    'echeances.col.type': 'Type',
    'echeances.col.item': 'Élément',
    'echeances.col.project': 'Projet',
    'echeances.emptyTitle': 'Aucune échéance',
    'echeances.emptyDescFilter': 'Aucune échéance ne correspond à ces filtres.',
    'echeances.emptyDescDefault': 'Rien de planifié pour le moment.',
    'echeances.countOne': '{n} échéance',
    'echeances.countMany': '{n} échéances',
  },
  en: {
    'gerance.title': 'Management',
    'gerance.subtitle': 'Consolidated deadline view across all projects',
    'gerance.view.calendar': 'Calendar view',
    'gerance.view.list': 'List view',
    'gerance.view.involvements': 'Involvements',

    // [Proposition: implications]
    'involvements.intro': 'Tasks and projects someone else involved you in. You are also told about them by e-mail.',
    'involvements.introAdmin': 'Who was involved in which task or project, with which role, and by whom. Each person is also told by e-mail.',
    'involvements.type.task': 'Tasks',
    'involvements.type.project': 'Projects',
    'involvements.allPersons': 'All people',
    'involvements.projectItem': 'The project',
    'involvements.col.date': 'Date',
    'involvements.col.person': 'Person',
    'involvements.col.item': 'Involvement',
    'involvements.col.role': 'Role',
    'involvements.col.by': 'By',
    'involvements.emptyTitle': 'No involvements',
    'involvements.emptyDesc': 'Nothing to show yet.',

    'gerancemeta.emptyTitle': 'No active projects',
    'gerancemeta.emptyDesc': 'No projects currently in Planning or Execution.',
    'gerancemeta.tasksTitle': 'Open tasks ({n})',
    'gerancemeta.taskBlocked': 'Blocked task',
    'gerancemeta.milestoneStateDone': 'Completed milestone',
    'gerancemeta.milestoneUrgencyGreen': 'In progress · on track',
    'gerancemeta.milestoneUrgencyOrange': 'In progress · due soon',
    'gerancemeta.milestoneUrgencyRed': 'In progress · overdue',
    'gerancemeta.milestoneStatePending': 'Upcoming milestone',
    'gerancemeta.projectDeadline': 'Project deadline',
    'gerancemeta.rcajoScoreLine': 'RCAJO:',
    'gerancemeta.rcajoNotLinked': 'Not linked',
    'gerancemeta.rcajoNoScore': 'No score yet',
    'gerancemeta.milestoneInfoBtn': 'View milestone details',
    'gerancemeta.milestoneDates': '{start} → {end}',
    'gerancemeta.milestoneNoDescription': 'No description.',
    'gerancemeta.milestoneOpenTasks': 'Open tasks',
    'gerancemeta.milestoneNoOpenTasks': 'No open tasks.',
    'gerancemeta.viewProject': 'View project',
    'gerancemeta.markMeeting': 'Meeting held',
    'gerancemeta.markTaskDone': 'Task done',
    'gerancemeta.markMeetingsTitle': '{n} meeting(s)',
    'gerancemeta.markTasksDoneTitle': '{n} task(s) moved to Done',
    'gerancemeta.timeSliderHint': 'Go back in time: the view is redrawn as it stood on that day',
    'gerancemeta.timeSliderReset': 'Back to today',
    'gerancemeta.absentAt': 'On {date} this project was not in Planning/Execution (stage: {stage})',
    'gerancemeta.notCreatedAt': 'On {date} this project did not exist yet',
    'gerancemeta.cardMeetings': 'Meetings ({n})',
    'gerancemeta.cardTasksDone': 'Tasks done ({n})',
    'gerancemeta.cardMore': '+ {n} more',
    'gerancemeta.railTotalsHint': 'Meetings held and tasks done since {date}',

    'echeances.type.project': 'Project',
    'echeances.type.milestone': 'Milestone',
    'echeances.type.task': 'Task',
    'echeances.filterAllTypes': 'All',
    'echeances.filterAllProjects': 'All projects',
    'echeances.overdueOnly': 'Overdue only',
    'echeances.stat.overdue': 'Overdue',
    'echeances.stat.overdueHint': 'Show overdue deadlines only',
    'echeances.daysLate': '+{n} d',
    'echeances.stat.dueWeek': 'Due this week',
    'echeances.stat.dueMonth': 'Due this month',
    'echeances.col.date': 'Deadline',
    'echeances.col.type': 'Type',
    'echeances.col.item': 'Item',
    'echeances.col.project': 'Project',
    'echeances.emptyTitle': 'No deadlines',
    'echeances.emptyDescFilter': 'No deadlines match these filters.',
    'echeances.emptyDescDefault': 'Nothing scheduled yet.',
    'echeances.countOne': '{n} deadline',
    'echeances.countMany': '{n} deadlines',
  },
};
